import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_RANGE, resolveDateRange } from "./dateRanges";

/**
 * Toàn bộ trạng thái của màn danh sách Payment Request nằm trong URL
 * (tab / search / filter / trang / sort), không giữ trong useState.
 *
 * Lý do: trước đây filter là state cục bộ nên F5 hoặc bấm Back là mất hết, và
 * không copy link gửi cho người khác được. Đẩy lên query param thì Back/Forward
 * chạy đúng và link chia sẻ được. (HR Hub cũng chưa làm bước này — mọi trang
 * bên đó vẫn mất filter khi refresh.)
 */

export const TABS = {
  MINE: "mine",
  ALL: "all",
  INBOX: "inbox",
  APPROVED: "approved",
  REJECTED: "rejected",
  UNPAID: "unpaid",
};

// Tab đọc từ GET /payment_requests?scope=… (inbox có endpoint riêng).
// Giá trị = scope phía BE (BaseController::LIST_SCOPES).
export const LIST_SCOPE_TABS = [TABS.MINE, TABS.ALL, TABS.APPROVED, TABS.REJECTED, TABS.UNPAID];

export const DEFAULT_PER_PAGE = 25;
export const PER_PAGE_OPTIONS = [25, 50, 100];

// Filter dạng mảng — lưu trong URL thành "1,2,3" cho gọn và dễ đọc.
const ARRAY_KEYS = ["status", "form_type", "department", "requestor", "priority"];

const readArray = (params, key) =>
  (params.get(key) || "").split(",").map((v) => v.trim()).filter(Boolean);

export default function usePaymentRequestQuery() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(() => ({
    tab:        Object.values(TABS).includes(searchParams.get("tab")) ? searchParams.get("tab") : TABS.MINE,
    q:          searchParams.get("q") || "",
    status:     readArray(searchParams, "status").map(Number).filter((n) => !Number.isNaN(n)),
    form_type:  readArray(searchParams, "form_type"),
    department: readArray(searchParams, "department"),
    priority:   readArray(searchParams, "priority"),
    requestor:  readArray(searchParams, "requestor").map(Number).filter((n) => !Number.isNaN(n)),
    // Khoảng ngày tạo phiếu: preset (?range=) hoặc tự chọn (?from=&to=); mặc định This year.
    date:       resolveDateRange({
      range: searchParams.get("range"), from: searchParams.get("from"), to: searchParams.get("to"),
    }),
    page:       Math.max(1, Number(searchParams.get("page")) || 1),
    per_page:   Number(searchParams.get("per_page")) || DEFAULT_PER_PAGE,
    sort:       searchParams.get("sort") || "",
    dir:        searchParams.get("dir") || "",
  }), [searchParams]);

  /**
   * Ghi đè một phần query. Mọi thay đổi filter đều tự về trang 1 — trừ khi
   * chính `page` đang được set (quy ước lấy từ HR Hub: setPage(1) trong mọi
   * onChange của filter, ở đây gom về một chỗ để không quên).
   *
   * Giá trị rỗng / mảng rỗng thì xoá hẳn param cho URL sạch.
   */
  const setQuery = useCallback((patch, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      Object.entries(patch).forEach(([key, value]) => {
        const isEmpty = value == null
          || value === ""
          || (Array.isArray(value) && value.length === 0);

        if (isEmpty) next.delete(key);
        else next.set(key, Array.isArray(value) ? value.join(",") : String(value));
      });

      if (!("page" in patch)) next.delete("page");

      return next;
    }, { replace });
  }, [setSearchParams]);

  // Xoá filter nhưng GIỮ tab — người dùng bấm "Clear" là muốn bỏ lọc, không
  // phải nhảy về tab khác.
  const resetFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const tab = prev.get("tab");
      const perPage = prev.get("per_page");
      if (tab) next.set("tab", tab);
      if (perPage) next.set("per_page", perPage);
      return next;
    });
  }, [setSearchParams]);

  /**
   * Params gửi lên API. Cũng là nguồn duy nhất để đếm số filter đang bật:
   * activeFilterCount = số key ở đây (trừ q, vốn có ô search riêng).
   */
  const apiParams = useMemo(() => {
    const params = { page: query.page, per_page: query.per_page };
    if (LIST_SCOPE_TABS.includes(query.tab)) params.scope = query.tab;
    if (query.q) params.q = query.q;
    ARRAY_KEYS.forEach((key) => {
      const value = key === "requestor" ? query.requestor : query[key];
      if (value.length) params[key === "requestor" ? "requestor_id" : key] = value;
    });
    if (query.date.from) params.date_from = query.date.from;
    if (query.date.to) params.date_to = query.date.to;
    if (query.sort) { params.sort = query.sort; params.direction = query.dir || "desc"; }
    return params;
  }, [query]);

  const activeFilterCount = useMemo(() => (
    ARRAY_KEYS.reduce((n, key) => {
      const value = key === "requestor" ? query.requestor : query[key];
      return n + (value.length ? 1 : 0);
    }, 0) + (query.date.range !== DEFAULT_RANGE ? 1 : 0)
  ), [query]);

  // Preset → ghi range, xoá from/to; khoảng tự chọn → ghi from/to, xoá range.
  // Chọn lại mặc định thì xoá hết cho URL sạch.
  const setDateRange = useCallback(({ range, from, to }) => {
    if (range) setQuery({ range: range === DEFAULT_RANGE ? "" : range, from: "", to: "" });
    else setQuery({ range: "", from, to });
  }, [setQuery]);

  // Badge từng tab đi theo đúng khoảng ngày của list (không theo filter khác).
  const dateParams = useMemo(() => {
    const params = {};
    if (query.date.from) params.date_from = query.date.from;
    if (query.date.to) params.date_to = query.date.to;
    return params;
  }, [query.date.from, query.date.to]);

  return { query, setQuery, resetFilters, apiParams, activeFilterCount, setDateRange, dateParams };
}
